import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { DataService } from '../services/data.service';
import { EntityViewService } from '../services/entity-view.service';
import { BuildService, BuildStatusSseEvent } from './services/build.service';
import { BuildType } from '../enums/build-type';
import { PromotionService } from '../services/promotion.service';
import { environment } from 'src/environments/environment';

export { BuildStatusSseEvent };

@Component({
  selector: 'build-data',
  templateUrl: './build-data.component.html',
  styleUrls: ['./build-data.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuildDataComponent implements OnInit, OnChanges, OnDestroy
{
  @Input() parentEntity: any;
  @Input() buildData: any;
  @Input() buildStatus: any;
  @Input() buildType: any;
  @Input() entity: any;
  @Input() type: any;
  @Input() hideBuildTimeHistory: boolean = false;
  @Input() isCustomBuildData: boolean = false;
  @Input() customPath: string | null = null;
  @Output() triggerRefreshEvent = new EventEmitter<any>();

  private streamSub: Subscription | null = null;
  private silentRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private silentRefreshInFlight = false;
  get buildTypes(): typeof BuildType
  {
    return BuildType;
  }
  isLoading: boolean = false;
  isLive: boolean = false;
  /** Drives the LIVE pill: 'building' while in progress, then 'finished'/'failed' briefly. */
  liveState: 'building' | 'finished' | 'failed' | null = null;
  /** Pill state: explicit result, else derived from the loaded build rows. */
  get pillState(): 'building' | 'finished' | 'failed' | null {
    return this.liveState ?? (this.hasActiveBuild() ? 'building' : null);
  }
  private liveResultTimer: ReturnType<typeof setTimeout> | null = null;
  isRefreshing: boolean = false;
  displayDataModal: boolean = false;
  isPromoting: boolean = false;
  promotionDisabledMessage: string = "Promotion Blocked: Item Content and Art status must be approved, and have a start date.";


  constructor(
    public entityViewService: EntityViewService,
    private dataService: DataService,
    public buildService: BuildService,
    private promotionService: PromotionService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {}

  /**
   * Build Data Component Initialization
   */
  ngOnInit() {
    this.openBuildStatusStream();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['buildData'] && this.hasHydratedBuildRows()) {
      this.updateLiveState();
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy() {
    this.closeBuildStatusStream();
  }

  /**
   * TrackBy function for build cards ngFor — prevents DOM nodes from being
   * recreated on each auto-refresh cycle.
   */
  trackByBuildId(_index: number, build: any): string {
    return build._id;
  }

  /**
   * Retrieves the color from the build bundle file size based on a treshold.
   *
   * @param status Build status.
   */
  getColorFromFileSize(build: any) {
    let sizeString = this.getSize(build.files);
    sizeString = sizeString.replaceAll(" ","");
    sizeString = sizeString.substring(0,sizeString.lastIndexOf("KB"));
    let size = parseFloat(sizeString);

    if(build.path.includes('levels')){
      if ((size > 3000) || (size < 500) ){
        return 'var(--gray-900)';
      } else {
        return 'var(--gray-100)';
      }
    } else if (build.path.includes('images/challenge_feed' )){
      if ( (size > 1100) || (size < 100) ){
        return 'var(--gray-900)';
      } else {
        return 'var(--gray-100)';
      }
    } else if (build.path.includes('items' )){
      if ( (size > 8000) || (size < 250)){
        return 'var(--gray-900)';
      } else {
        return 'var(--gray-100)';
      }
    } else if (build.path.includes('images/item_thumbs' )){
      if ( (size > 1100) || (size < 100) ){
        return 'var(--gray-900)';
      } else {
        return 'var(--gray-100)';
      }
    } else {
      return 'var(--gray-100)';
    }

  }

  /**
   * returns the value of the first key (angular had trouble doing it.)
   */
  getSize(obj: any) {
    return obj[Object.keys(obj)[0]];
  }


  /**
   * Opens /api/build/stream with the JWT on the Authorization header.
   * Native EventSource cannot set headers, so this uses fetch like notifications.
   */
  private openBuildStatusStream() {
    this.closeBuildStatusStream();
    this.streamSub = this.buildService.connectBuildStatusStream(this.entity?.id).subscribe({
      next: (payload) => {
        this.ngZone.run(() => {
          if (!this.isLive) {
            this.isLive = true;
            this.cdr.markForCheck();
            if (this.canRefreshBuildRecords()) {
              this.scheduleSilentRefresh();
            }
          }
          this.updateLiveState();
          this.applyBuildStatusEvent(payload);
        });
      },
      error: () => {
        this.ngZone.run(() => {
          this.isLive = false;
          this.cdr.markForCheck();
        });
      },
      complete: () => {
        this.ngZone.run(() => {
          this.isLive = false;
          this.cdr.markForCheck();
        });
      },
    });
  }

  private closeBuildStatusStream() {
    if (this.silentRefreshTimer) {
      clearTimeout(this.silentRefreshTimer);
      this.silentRefreshTimer = null;
    }
    this.clearLiveResultTimer();
    this.liveState = null;
    this.streamSub?.unsubscribe();
    this.streamSub = null;
    this.isLive = false;
  }

  private hasHydratedBuildRows(): boolean {
    return Array.isArray(this.buildData) && this.buildData.length > 0;
  }

  private canRefreshBuildRecords(): boolean {
    if (this.hasHydratedBuildRows()) {
      return true;
    }
    return !!this.buildData && !Array.isArray(this.buildData) && Object.keys(this.buildData).length > 0;
  }

  /**
   * Updates the matching platform row in place from an SSE payload.
   */
  applyBuildStatusEvent(raw: string | BuildStatusSseEvent) {
    let payload: BuildStatusSseEvent;
    try {
      payload = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      return;
    }

    if (!payload || payload.type === 'heartbeat' || payload.entityId == null || !payload.status) {
      return;
    }

    if (!this.hasHydratedBuildRows()) {
      return;
    }

    const entityId = this.entity?.id ?? this.buildData[0]?.entityId;
    if (entityId != null && String(payload.entityId) !== String(entityId)) {
      return;
    }
    if (this.buildType != null && payload.buildType != null && Number(payload.buildType) !== Number(this.buildType)) {
      return;
    }

    let matches = payload.recordId
      ? this.buildData.filter((build: any) => build._id === payload.recordId)
      : [];
    if (matches.length === 0 && payload.platform) {
      matches = this.buildData.filter(
        (build: any) => Array.isArray(build.platforms) && build.platforms.includes(payload.platform),
      );
    }

    if (matches.length === 0) {
      return;
    }

    const now = new Date().toISOString();
    const status = payload.status;
    for (const build of matches) {
      build.status = status;
      if (status === 'building' && !build.startedAt) {
        build.startedAt = now;
      }
      if ((status === 'finished' || status === 'failed') && !build.finishedAt) {
        build.finishedAt = now;
      }
    }

    if (this.entity) {
      this.entity.buildStatus = this.buildService.getBuildStatus(this.buildData);
    }

    this.updateLiveState();
    this.cdr.markForCheck();

    if (status === 'finished' || status === 'failed') {
      this.scheduleSilentRefresh();
    }
  }

  /**
   * Building -> blue pill. When the last active build ends, show the result
   * (green/red) for a few seconds and then hide the pill.
   */
  private updateLiveState() {
    if (this.hasActiveBuild()) {
      this.clearLiveResultTimer();
      this.liveState = 'building';
      return;
    }
    if (this.liveState !== 'building') {
      return;
    }
    const hasFailed = Array.isArray(this.buildData) &&
      this.buildData.some((b: any) => b.status?.toLowerCase() === 'failed');
    this.liveState = hasFailed ? 'failed' : 'finished';
    this.clearLiveResultTimer();
    this.liveResultTimer = setTimeout(() => {
      this.liveResultTimer = null;
      this.liveState = null;
      this.cdr.markForCheck();
    }, 5000);
  }

  private clearLiveResultTimer() {
    if (this.liveResultTimer) {
      clearTimeout(this.liveResultTimer);
      this.liveResultTimer = null;
    }
  }

  private scheduleSilentRefresh() {
    if (this.silentRefreshTimer) {
      clearTimeout(this.silentRefreshTimer);
    }
    this.silentRefreshTimer = setTimeout(() => {
      this.silentRefreshTimer = null;
      void this._doRefresh(true);
    }, 300);
  }

  /**
   * Shared refresh logic used by both manual and silent refresh paths.
   * @param silent When true, skips the manual-refresh spinner.
   */
  private async _doRefresh(silent: boolean): Promise<void> {
    if (!this.entity) {
      return;
    }
    if (silent && this.silentRefreshInFlight) {
      return;
    }
    if (silent) {
      this.silentRefreshInFlight = true;
    }

    if (!silent) {
      this.isRefreshing = true;
    }

    try {
      if (!this.isCustomBuildData) {
        let result = await this.buildService.getBuildData(this.buildData);
        if (result) {
          this.entity.buildData = result.buildData;
          this.entity.buildStatus = result.buildStatus;
        }
      }

      if (this.type == 'items' || this.type == 'challenges') {
        await this.dataService.getFileSizeData(this.entity.id, this.type);
      }

      this.triggerRefreshEvent.emit(this.entity);
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      if (silent) {
        this.silentRefreshInFlight = false;
      }
      if (!silent) {
        this.isRefreshing = false;
      }
      this.cdr.markForCheck();
    }
  }

  /**
   * Check for new build data (manual refresh)
   */
  async getBuildData() {
    await this._doRefresh(false);
  }

  /**
   * Sets the plaform name to display on the detailed data
   * table.
   *
   * @param platform The current platform.
   */
  setPlatformName(platform: string) {
    switch (platform) {
      case 'and':
        return 'Android';
      case 'ios':
        return 'IOS';
      case 'lin':
        return 'Linux';
      case 'mac':
        return 'MAC';
      case 'win':
        return 'Windows';

      default:
        return '';
    }
  }

  /**
   * Sets the build status color.
   *
   * @param status The current status of the build.
   */
  setPlatformStatusColor(status: string) {
    status = status.toLowerCase();
    switch (status) {
      case 'finished':
        return 'success';
      case 'failed':
        return 'danger';
      case 'building':
        return 'warning';
      case 'queued':
        return 'secondary';
      case 'stalled':
        return 'warning';
      default:
        return 'secondary';
    }
  }

  /**
   * Handle Promotion of Assets and Images
   */
  async onPromote(promoteBoth: boolean = false, newHash: boolean = false) {
    // Prevent multiple simultaneous promotion calls
    if (this.isPromoting) {
      return;
    }

    this.isPromoting = true;

    try {
      let recordPayload = {
        from: 'dev',
        to: 'prod',
        entity: this.entity,
        buildType: this.buildType,
        newHash: newHash
      };

      if (promoteBoth) {
        if (this.type == 'items' || this.type == 'challenges') {
          let asset;
          let image;

          if (this.type == 'items' && this.parentEntity) {
            asset = this.parentEntity.prefab_ref;
            image = this.parentEntity.thumbnail_ref;
          } else if (this.type == 'challenges' && this.parentEntity) {
            asset = this.parentEntity.scene_ref;
            image = this.parentEntity.image_ref;
          }

          // Promote Asset
          if (asset && asset.id && asset.path) {
            await this.promotionService.onPromoteRecord(
              this.type == 'items' ? BuildType.Items : BuildType.Levels,
              {
                from: 'dev', to: 'prod',
                entity: asset, buildType: this.type == 'items' ? BuildType.Items : BuildType.Levels,
                newHash: newHash
              },
              true,
              this.type
            );
          }
          // Promote Image
          if (image && image.id && image.path) {
            await this.promotionService.onPromoteRecord(
              BuildType.Images,
              {
                from: 'dev', to: 'prod',
                entity: image, buildType: BuildType.Images,
                newHash: newHash
              },
              true,
              this.type
            );
          }
        } else {
          await this.promotionService.onPromoteRecord(this.buildType, recordPayload, true, this.type);
        }
      } else {
        await this.promotionService.onPromoteRecord(this.buildType, recordPayload, true, this.type);
      }
      await this.getBuildData();
    } catch (error) {
      console.error('Promotion failed:', error);
    } finally {
      this.isPromoting = false;
      this.cdr.markForCheck();
    }
  }

  /** True while any platform build is queued or building (drives the LIVE pill). */
  hasActiveBuild(): boolean {
    return Array.isArray(this.buildData) && this.buildData.some((b: any) =>
      ['queued', 'building'].includes(b.status?.toLowerCase())
    );
  }

  isPromotionDisabled() {
    if (environment.env == "test") {
      this.promotionDisabledMessage = "Promotion disabled in test environment";
      return true;
    }

    // Block promotion while any non-web platform build is in-progress or failed.
    if (this.buildData && Array.isArray(this.buildData)) {
      const nonWebBuilds = this.buildData.filter((b: any) => !b.platforms?.includes('web'));
      const hasInProgress = nonWebBuilds.some((b: any) =>
        ['queued', 'building', 'stalled'].includes(b.status?.toLowerCase())
      );
      const hasFailed = nonWebBuilds.some((b: any) => b.status?.toLowerCase() === 'failed');

      if (hasInProgress) {
        this.promotionDisabledMessage = "Promotion Blocked: Build is currently in progress.";
        return true;
      }
      if (hasFailed) {
        this.promotionDisabledMessage = "Promotion Blocked: One or more platform builds failed.";
        return true;
      }
    }

    if (this.buildType == this.buildTypes.Items) {
      if (this.parentEntity && this.parentEntity.itemStatus == 'Approved' && this.parentEntity.vendorStatus == 'Approved' && this.parentEntity.start != null) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }
}
